<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';

import ConversationIndexStatusFilterArrow from './ConversationIndexStatusFilterArrow.vue';
import ConversationIndexDispositionChip from '@/components/Conversations/ConversationIndex/Filters/ConversationIndexDispositionChip.vue';

import {
  JobApplicantStatus,
  type JobApplicantStatusCount,
  type ProjectWithRelations,
} from '@factoryfixinc/ats-interfaces';
import ProjectService from '@/core/shared/project/project.service';
import { APPLICATION_STATUS_FUNNEL } from '@/core/conversations/conversation-index/global-search-labels.util';
import CopilotBubbleSvg from '@/assets/svg/copilot-bubble.svg';

// Scroll distance between status filter options
const SCROLL_JUMP = 180;

const props = defineProps<{
  project?: ProjectWithRelations<'candidates'>;
}>();

const emit = defineEmits<{
  (e: 'update:status', payload: string): void;
}>();

const conversationIndexService = new ConversationIndexService();
const projectService = new ProjectService();

const filterContentWrapper = ref<HTMLElement | null>(null);
const showPreviousButton = ref(false);
const showNextButton = ref(true);
// Order comes from the app's one shared status list; labels come from the
// pane's stage-label map, so the chips can never diverge from the account-wide
// search pills.
const statusFilterBaseOptions = APPLICATION_STATUS_FUNNEL.map(({ status, label }) => ({
  text: label,
  value: status,
  icon: status === JobApplicantStatus.CONTACT ? CopilotBubbleSvg : undefined,
}));

const currentProjectJobId = computed(() => projectService.currentProject?.jobId);
const statusFilterOptions = computed(() => statusFilterBaseOptions);
const currentProject = computed(() => {
  return props.project;
});

const selectedStatusList = computed({
  get: () => conversationIndexService.selectedStatusList,
  set: (statusList: string[]) => {
    conversationIndexService.selectedStatusList = statusList;
  },
});

async function setConversationIndexStatusSearch(statusList: string[]) {
  // Don't fetch if no project is selected yet
  if (!currentProjectJobId.value) {
    return;
  }

  // Update local state only - let the jobs page handle navigation and data loading
  conversationIndexService.selectedStatusFilters = statusList;
  selectedStatusList.value = statusList;
}

function isSelected(statusFilterOption: string) {
  return selectedStatusList.value.includes(statusFilterOption);
}

function getStatusCandidatesCount(statusFilterOption: keyof JobApplicantStatusCount) {
  if (projectService.isProjectFromDeepLinkRemoved) {
    return 0;
  }

  if (projectService.areNoProjectsAvailable) {
    // When there are no projects available, the status filter should not be shown
    return 0;
  }

  const candidateCounts = currentProject.value?.candidates;
  let count = 0;

  if (candidateCounts) {
    count = candidateCounts[statusFilterOption] ?? 0;
  }

  return count;
}

/**
 * We remove multi-select functionality when we select a Status Filter but we
 * leave the rest of the code without any changes in case we need to get back
 * multi-select again.
 */
function selectStatus(statusFilterOptionSelected: string) {
  emit('update:status', statusFilterOptionSelected);

  if (isSelected(statusFilterOptionSelected)) {
    selectedStatusList.value = selectedStatusList.value.filter(
      (selectedStatusFilterOption) => selectedStatusFilterOption !== statusFilterOptionSelected,
    );
  } else {
    selectedStatusList.value = [statusFilterOptionSelected];
  }
  setConversationIndexStatusSearch(selectedStatusList.value);
}

function onFilterContentScroll() {
  updateArrowButtons(filterContentWrapper.value?.scrollLeft ?? 0);
}

function updateArrowButtons(newScrollLeft: number) {
  const arrowButtonToleranceToHide = 20;

  const filterContentWrapperElement = filterContentWrapper.value as HTMLElement;
  showPreviousButton.value = newScrollLeft > arrowButtonToleranceToHide;

  showNextButton.value =
    newScrollLeft + filterContentWrapperElement.clientWidth + arrowButtonToleranceToHide <
    filterContentWrapperElement.scrollWidth;
}

function navigationFilterHandler(leftScrollBy: number) {
  filterContentWrapper.value?.scrollBy({
    left: leftScrollBy,
  });
}

onMounted(() => {
  // Only handle the scroll behavior - no data loading or filter initialization
  const filterContentWrapperElement = filterContentWrapper.value as HTMLElement;
  filterContentWrapperElement.addEventListener('wheel', (e) => {
    e.preventDefault();
    const newScrollLeft = filterContentWrapperElement.scrollLeft + e.deltaX;
    filterContentWrapperElement.style.scrollBehavior = 'auto';
    filterContentWrapperElement.scrollLeft = newScrollLeft;
    filterContentWrapperElement.style.scrollBehavior = 'smooth';
  });
});

onBeforeUnmount(() => {
  const filterContentWrapperElement = filterContentWrapper.value as HTMLElement;
  filterContentWrapperElement.removeEventListener('wheel', () => {});
});
</script>

<template>
  <div class="relative">
    <ConversationIndexStatusFilterArrow
      :visible="showPreviousButton"
      id="arrow-button-left"
      data-test-id="arrow-button-left"
      direction="left"
      @click="navigationFilterHandler(SCROLL_JUMP * -1)"
    />
    <div
      class="relative flex scroll-mb-0 space-x-2 overflow-hidden scroll-smooth px-2"
      ref="filterContentWrapper"
      @scroll="onFilterContentScroll"
    >
      <ConversationIndexDispositionChip
        v-for="statusFilterOption in statusFilterOptions"
        :key="statusFilterOption.value"
        :is-selected="isSelected(statusFilterOption.value)"
        :text="statusFilterOption.text"
        :project="currentProject"
        :count="getStatusCandidatesCount(statusFilterOption.value as keyof JobApplicantStatusCount)"
        :icon="statusFilterOption.icon"
        @click="selectStatus(statusFilterOption.value)"
      />
    </div>
    <ConversationIndexStatusFilterArrow
      :visible="showNextButton"
      id="arrow-button-right"
      data-test-id="arrow-button-right"
      direction="right"
      @click="navigationFilterHandler(SCROLL_JUMP)"
    />
  </div>
</template>
